<script setup lang="ts">
import type { PropertyLocationSectionProps } from '.';

import GmapMap from '~/core/service/googleMaps/components/GmapMap/GmapMap.vue';
import GmapQuadrilateral from '~/core/service/googleMaps/components/GmapQuadrilateral/GmapQuadrilateral.vue';

import { getQuadrilateral } from '~/core/service/googleMaps/pluscode';
import messages from './PropertyLocationSection.locale.json';
import TextLink from '~/components/ui/text-link/TextLink.vue';

const { pluscodeGroup } = defineProps<PropertyLocationSectionProps>();

const emit = defineEmits<{
	(e: 'openMapModal', payload: { ctaText: string }): void;
}>();

const quadrilateral = computed(() => getQuadrilateral(pluscodeGroup, 1));

const { t } = useI18n({
	useScope: 'local',
	messages
});
</script>

<template>
	<div class="flex w-full flex-col gap-4 py-12">
		<div class="flex flex-col gap-4 md:gap-6">
			<Skeleton
				:is-active="isLoading"
				variant="white"
				class="h-auto min-h-[22px] w-[200px]"
			>
				<h2
					class="font-matter-sq text-[28px] font-normal leading-[42px] text-primary"
				>
					{{ t('title') }}
				</h2>
			</Skeleton>

			<div class="flex w-full flex-col justify-between gap-4 md:flex-row">
				<Skeleton
					:is-active="isLoading"
					variant="white"
					class="min-h-5 min-w-[188px]"
				>
					<span class="text-base text-primary">
						{{ `${region}, ${city} - ${state}` }}
					</span>
				</Skeleton>

				<Skeleton
					:is-active="isLoading"
					variant="white"
					class="h-auto min-h-5 min-w-[160px]"
				>
					<TextLink
						class="flex items-center gap-1"
						@click="emit('openMapModal', { ctaText: t('cta') })"
					>
						<IconMaterialSymbolsLightMapOutline class="size-5 text-primary" />
						{{ t('cta') }}
					</TextLink>
				</Skeleton>
			</div>
		</div>

		<ClientOnly fallback-tag="div">
			<template #fallback>
				<div
					class="relative size-full bg-map-placeholder-image bg-cover bg-no-repeat"
				/>
			</template>

			<Skeleton
				v-if="isLoading"
				:is-active="isLoading"
				variant="white"
				class="h-[340px] w-full"
			/>

			<GmapMap
				v-else
				:center="quadrilateral.centerCoordinates"
				:zoom="14"
				:zoom-control="false"
				:draggable="false"
				:min-zoom="14"
				class="h-[320px] md:h-[340px]"
			>
				<GmapQuadrilateral
					:center-coordinates="null"
					:paths-coordinates="quadrilateral.pathsCoordinates"
					:count="1"
					:group-key="quadrilateral.groupKey"
					:is-selected="true"
					:is-clickable="false"
				/>
			</GmapMap>
		</ClientOnly>
	</div>
</template>
